// src/components/pages/benefaction/Request/LandInfoPanel.tsx
import * as React from "react";
import type { LandInfoRows } from "../../../../utils/landInfoRows";

type Props = {
  t: (k: string, o?: any) => string;
  mapEmbedSrc: string;
  qrImgSrc: string;
  mapsDeeplink?: string;
  landLabel: string;
  landInfo: LandInfoRows;
  landPrice: number;
  numberSar: (v: number | null | undefined) => string;
  onNext?: () => void;
};

const nextCtaClass =
  "inline-flex items-center justify-center min-h-[48px] min-w-[200px] rounded-2xl bg-sa-600 px-10 py-3 text-sm font-semibold text-white shadow-md shadow-sa-600/25 transition hover:bg-sa-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

const cardHeaderClass =
  "border-b border-neutral-200/80 bg-gradient-to-l from-sa-50/90 via-white to-white px-5 py-3.5 text-right text-base font-semibold text-neutral-900";

const landCardHeaderClass =
  "border-b border-neutral-200/80 bg-gradient-to-l from-sa-50/90 via-white to-white px-5 py-3.5 text-center text-base font-semibold text-neutral-900";

function InfoRow({ label, value }: { label: string; value: string }) {
  const display = value?.trim() ? value.trim() : "—";
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-neutral-100 py-2.5 last:border-0 sm:grid-cols-[minmax(7.5rem,10rem)_1fr] sm:items-baseline sm:gap-4">
      <dt className="text-sm font-medium text-neutral-500">{label}</dt>
      <dd className="text-sm font-semibold leading-relaxed text-neutral-900 break-words">{display}</dd>
    </div>
  );
}

const LandInfoPanel: React.FC<Props> = ({
  t,
  mapEmbedSrc,
  qrImgSrc,
  mapsDeeplink,
  landLabel,
  landInfo,
  landPrice,
  numberSar,
  onNext,
}) => {
  return (
    <div className="flex flex-col gap-5" dir="rtl">
      {/* Land info card */}
      <div className="w-full overflow-hidden rounded-3xl border border-neutral-200/90 bg-white shadow-lg shadow-neutral-200/40 ring-1 ring-black/[0.03]">
        <div className={landCardHeaderClass}>{t("landInfo", "بيانات الأرض الشاغرة لبناء مسجد")}</div>
        <div className="px-5 py-5 sm:px-6 sm:py-6">
          <div className="overflow-hidden rounded-2xl border border-neutral-200/90 bg-gradient-to-br from-neutral-50/70 via-white to-white shadow-inner">
            <div className="divide-y divide-neutral-200/80">
              <div className="p-4 sm:p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 text-right">
                    <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                      {t("landNumber", "رقم الأرض")}
                    </div>
                    <div className="mt-1.5 break-words text-lg font-bold tracking-tight text-neutral-900">
                      {landLabel}
                    </div>
                  </div>

                  {qrImgSrc ? (
                    <div className="shrink-0 rounded-xl bg-white p-1.5 shadow-sm ring-1 ring-neutral-200/80">
                      <img
                        src={qrImgSrc}
                        alt=""
                        width={120}
                        height={120}
                        className="h-24 w-24 rounded-lg"
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <h3 className="mb-3 text-right text-sm font-bold text-neutral-900">
                  {t("landDetailsSection", "معلومات الأرض")}
                </h3>
                <dl>
                  <InfoRow label={t("serialNumber", "الرقم التسلسلي")} value={landInfo.sequence} />
                  <InfoRow label={t("region", "المنطقة")} value={landInfo.region} />
                  <InfoRow label={t("city", "المدينة")} value={landInfo.city} />
                  <InfoRow label={t("center", "المركز")} value={landInfo.center} />
                  <InfoRow label={t("district", "الحي")} value={landInfo.district} />
                  <InfoRow label={t("fullAddress", "العنوان الكامل")} value={landInfo.fullAddress} />
                  <InfoRow label={t("donationType", "نوع المساهمة")} value={landInfo.donationType} />
                </dl>
              </div>

              <dl className="bg-gradient-to-l from-emerald-50/90 via-white/80 to-sa-50/30 p-4 sm:flex sm:items-center sm:justify-between sm:gap-4 sm:p-5">
                <dt className="text-right text-sm font-semibold text-emerald-950/90 sm:text-base">
                  {t("amountsTitle", "القيمة التقديرية للبناء")}
                </dt>
                <dd className="mt-3 flex items-center justify-end gap-2 sm:mt-0">
                  <span className="text-2xl font-bold tabular-nums text-neutral-900 sm:text-3xl">{numberSar(landPrice)}</span>
                  <span className="inline-flex items-center">
                    <img
                      className="riyal-icon h-6 w-6 sm:h-7 sm:w-7"
                      src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                      alt="ريال سعودي"
                      onError={(e) => {
                        e.currentTarget.replaceWith(
                          Object.assign(document.createElement("span"), {
                            textContent: "﷼",
                            ariaLabel: "ريال سعودي",
                          }) as unknown as Node
                        );
                      }}
                    />
                  </span>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Map card */}
      <div className="w-full overflow-hidden rounded-3xl border border-neutral-200/90 bg-white shadow-lg shadow-neutral-200/40 ring-1 ring-black/[0.03]">
        <div className={cardHeaderClass}>{t("mapTitle", "موقع الأرض على الخريطة")}</div>
        <div className="px-5 py-5 sm:px-6 sm:py-6">
          {mapEmbedSrc ? (
            <div className="flex flex-col gap-4">
              <div className="min-w-0 flex-1 overflow-hidden rounded-2xl border border-neutral-200/80 shadow-inner">
                <div className="aspect-[4/3] min-h-[220px] w-full sm:min-h-[280px] lg:min-h-[320px]">
                  <iframe
                    title={t("mapIframeTitle", "موقع الأرض على خريطة تفاعلية")}
                    src={mapEmbedSrc}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="h-full w-full"
                  />
                </div>
              </div>
              {mapsDeeplink ? (
                <div className="w-full shrink-0">
                  <a
                    href={mapsDeeplink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-neutral-200 bg-white px-4 py-3.5 text-sm font-semibold text-neutral-800 shadow-sm transition hover:border-sa-300 hover:bg-sa-50/50 focus:outline-none focus:ring-2 focus:ring-sa-500"
                  >
                    <svg className="h-5 w-5 text-sa-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    {t("getDirections", "الحصول على الاتجاهات")}
                  </a>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="rounded-2xl border border-neutral-100 bg-neutral-50 px-4 py-8 text-center text-sm text-neutral-500">
              {t("noCoords", "لا تتوفر إحداثيات لهذه الأرض")}
            </div>
          )}

          {!mapEmbedSrc && mapsDeeplink ? (
            <a
              href={mapsDeeplink}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex w-full items-center justify-center rounded-2xl border-2 border-neutral-200 bg-white px-4 py-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
            >
              {t("getDirections", "الحصول على الاتجاهات")}
            </a>
          ) : null}
        </div>
      </div>

      {onNext ? (
        <div className="rounded-3xl border border-neutral-200/80 bg-gradient-to-b from-white to-neutral-50/80 px-5 py-6 shadow-md shadow-neutral-200/30 sm:px-8">
          <div className="flex flex-col items-center text-center">
            <button type="button" className={nextCtaClass} onClick={onNext}>
              {t("next", "التالي")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LandInfoPanel;
